import type { MapBounds } from '../components/HereMapContext'
import type { Location } from '../hooks/useHereLocation'

type GeoJsonPolygon = {
  type: 'Polygon'
  coordinates: number[][][]
}

type GeoJsonMultiPolygon = {
  type: 'MultiPolygon'
  coordinates: number[][][][]
}

type GeoJsonGeometry = GeoJsonPolygon | GeoJsonMultiPolygon

const rectToBounds = (rect: H.geo.Rect): MapBounds => ({
  north: rect.getTop(),
  south: rect.getBottom(),
  east: rect.getRight(),
  west: rect.getLeft(),
})

/**
 * Extracts outer ring coordinate arrays from a GeoJSON Polygon or MultiPolygon.
 * Each returned array is an outer ring: [[lng, lat], [lng, lat], ...].
 */
const extractOuterRings = (geometry: GeoJsonGeometry): number[][][] => {
  if (geometry.type === 'Polygon') {
    // Polygon coordinates: [outerRing, ...holes]
    return [geometry.coordinates[0]]
  }
  if (geometry.type === 'MultiPolygon') {
    // MultiPolygon coordinates: [[outerRing, ...holes], ...]
    return geometry.coordinates.map(polygon => polygon[0])
  }
  return []
}

/**
 * Computes a bounding box from an array of GeoJSON strings (Polygon or MultiPolygon).
 * Returns undefined if no valid bounds can be computed.
 */
export const computeBoundsFromGeoJson = (
  geoJsonStrings: string[],
): MapBounds | undefined => {
  if (geoJsonStrings.length === 0) return undefined

  const lineStrings: H.geo.LineString[] = []

  for (const jsonStr of geoJsonStrings) {
    try {
      const geometry: GeoJsonGeometry = JSON.parse(jsonStr)
      const outerRings = extractOuterRings(geometry)

      for (const ring of outerRings) {
        const lineString = new H.geo.LineString()
        for (const coord of ring) {
          // GeoJSON coordinates are [lng, lat]
          lineString.pushPoint({ lat: coord[1], lng: coord[0] })
        }
        lineStrings.push(lineString)
      }
    } catch {
      // Skip invalid JSON strings
    }
  }

  if (lineStrings.length === 0) return undefined

  const multiLineString = new H.geo.MultiLineString(lineStrings)
  const bbox = multiLineString.getBoundingBox()

  if (!bbox) return undefined

  return rectToBounds(bbox)
}

/**
 * Computes a bounding box from a list of locations.
 * Returns undefined if the list is empty or no bounds can be computed.
 */
export const computeBoundsFromLocations = (
  locations: Location[],
): MapBounds | undefined => {
  if (locations.length === 0) return undefined

  const points = locations.map(
    loc => new H.geo.Point(loc.latitude, loc.longitude),
  )
  const multiPoint = new H.geo.MultiPoint(points)
  const bbox = multiPoint.getBoundingBox()

  if (!bbox) return undefined

  return rectToBounds(bbox)
}
